import { Component } from '@theme/component';

/**
 * @typedef {object} CollapsibleTextRefs
 * @property {HTMLButtonElement} toggle - The toggle button element
 * @property {HTMLElement} content - The content container element
 */

export class CollapsibleText extends Component {
  /** @type {CollapsibleTextRefs} */
  refs = /** @type {CollapsibleTextRefs} */ ({});

  connectedCallback() {
    super.connectedCallback();

    this.#initializeCollapsible();
  }

  #initializeCollapsible() {
    const { toggle, content } = this.refs;
    
    if (!toggle || !content) {
      console.warn('Collapsible Text - Missing required elements');
      return;
    }

    this.isExpanded = false;
    this.showMoreText = toggle.dataset.showMore ?? 'More info +';
    this.showLessText = toggle.dataset.showLess ?? 'Show less -';
    
    // Check if content needs collapsing (more than one line)
    if (!this.#needsCollapsing(content)) {
      toggle.style.display = 'none';
      return;
    }
    
    // Set initial state
    content.setAttribute('data-collapsed', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = this.showMoreText;
    
    // Calculate and set the proper collapsed height based on content
    this.#setCollapsedHeight(content);
    
    // Add event listeners
    toggle.addEventListener('click', () => this.#toggleContent());
    toggle.addEventListener('keydown', (e) => this.#handleKeydown(e));
  }

  #needsCollapsing(/** @type {HTMLElement} */ content) {
    // Temporarily show all content to measure if it's more than one line
    const originalDisplay = content.style.display;
    const originalOverflow = content.style.overflow;
    const originalMaxHeight = content.style.maxHeight;
    
    content.style.display = 'block';
    content.style.overflow = 'visible';
    content.style.maxHeight = 'none';
    
    // Get the height of the content
    const contentHeight = content.scrollHeight;
    
    // Get the height of one line
    const firstElement = content.querySelector('h1, h2, h3, h4, h5, h6, p, div, span') || content;
    const computedStyle = window.getComputedStyle(firstElement);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const fontSize = parseFloat(computedStyle.fontSize);
    const oneLineHeight = lineHeight || fontSize * 1.2;
    
    // Restore original styles
    content.style.display = originalDisplay;
    content.style.overflow = originalOverflow;
    content.style.maxHeight = originalMaxHeight;
    
    // Return true if content is taller than one line
    return contentHeight > oneLineHeight;
  }

  #setCollapsedHeight(/** @type {HTMLElement} */ content) {
    // Temporarily show all content to measure the first line height
    const originalDisplay = content.style.display;
    const originalOverflow = content.style.overflow;
    
    content.style.display = 'block';
    content.style.overflow = 'visible';
    content.style.maxHeight = 'none';
    
    // Get the computed line height of the first element
    const firstElement = content.querySelector('h1, h2, h3, h4, h5, h6, p, div, span') || content;
    const computedStyle = window.getComputedStyle(firstElement);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const fontSize = parseFloat(computedStyle.fontSize);
    
    // Calculate the height for one line (use line-height if available, otherwise use font-size * 1.2)
    const oneLineHeight = lineHeight || fontSize * 1.2;
    
    // Set the collapsed height
    content.style.setProperty('--collapsed-height', `${oneLineHeight}px`);
    
    // Restore original styles
    content.style.display = originalDisplay;
    content.style.overflow = originalOverflow;
    content.style.maxHeight = '';
  }

  #toggleContent() {
    const { toggle, content } = this.refs;
    
    this.isExpanded = !this.isExpanded;
    
    if (this.isExpanded) {
      content.setAttribute('data-collapsed', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.textContent = this.showLessText ?? 'Show less -';
      // Remove height restriction when expanded
      content.style.maxHeight = 'none';
    } else {
      content.setAttribute('data-collapsed', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = this.showMoreText ?? 'More info +';
      // Apply calculated height when collapsed
      content.style.maxHeight = content.style.getPropertyValue('--collapsed-height') || '1.5em';
    }
  }

  #handleKeydown(/** @type {KeyboardEvent} */ event) {
    // Handle Enter and Space keys
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#toggleContent();
    }
  }
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}
